import { Box, Divider, Text } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { IAIArticleAnswer } from '../../../common/messageCreators';
import { selectIsDocumentVisible, selectIsMessageWasPresentOnPageLoad } from '../../../common/selectors/chat';
import { isHtmlHebrew } from '../../../util/language';
import { usePingLinkRef } from '../../hooks';
import { useDeepLinkClickListener } from '../../hooks/useDeepLinkClickListener';
import { useNavigateToArticleClickListener } from '../../hooks/useNavigateToArticleClickListener';
import { AnswerFeedback } from '../AnswerFeedback';
import { ExtraChatMessageProps } from '../Chat';
import { DirectionWrapper } from '../DirectionWrapper';
import { MessageBubble } from '../MessageBubble';
import { MessageItem } from '../MessageItem';
import { AIArticleAnswerDataHooks } from './AIArticleAnswer.dataHooks';
import { AIArticleAnswerItem } from './AIArticleAnswerItem';

type AIArticleAnswerProps = IAIArticleAnswer & ExtraChatMessageProps;

export const AIArticleAnswer: FC<AIArticleAnswerProps> = (props) => {
  const { id, answer, articles, isFirstInGroup, scrollToFn } = props;
  const { t } = useTranslation();
  const linkPing = usePingLinkRef<HTMLDivElement>();
  const isTabActive = useSelector(selectIsDocumentVisible);

  const isTabActiveOnFirstRender = useRef<boolean | undefined>();
  const isNew = !useSelector(selectIsMessageWasPresentOnPageLoad(id));

  const isRtl = useMemo(() => isHtmlHebrew(answer), [answer]);

  useEffect(() => {
    if (isTabActive !== undefined && isTabActiveOnFirstRender.current === undefined) {
      isTabActiveOnFirstRender.current = isTabActive;
    }
  }, [isTabActive]);

  const dangerouslySetInnerHTML = useMemo(() => {
    return {
      __html: sanitizeHtml(answer),
    };
  }, [answer]);

  const onLinkClick = useNavigateToArticleClickListener();
  useDeepLinkClickListener(linkPing, onLinkClick);

  return (
    <Box direction="vertical" gap="1">
      <MessageItem id={id} width="full">
        <MessageBubble isFirstInGroup={isFirstInGroup}>
          <DirectionWrapper isRtl={isRtl}>
            <Box direction="vertical" gap="2">
              <Box direction="vertical" whiteSpace="pre-wrap">
                <div
                  ref={linkPing}
                  dangerouslySetInnerHTML={dangerouslySetInnerHTML}
                  data-hook={AIArticleAnswerDataHooks.messageContent()}
                />
              </Box>
              <Divider />
              <Box direction="vertical" gap={1}>
                <Text size="tiny" weight="thin">
                  {t<TLocalesKeys>('ai_article_answer_more_info')}
                </Text>
                <Box direction="vertical" gap={1}>
                  {articles.map(({ articleId, title, url }) => (
                    <AIArticleAnswerItem key={articleId} articleId={articleId} title={title} url={url} />
                  ))}
                </Box>
              </Box>
            </Box>
          </DirectionWrapper>
        </MessageBubble>
      </MessageItem>
      {isNew && isTabActiveOnFirstRender.current && <AnswerFeedback messageId={id} scrollToFn={scrollToFn} />}
    </Box>
  );
};
